import React, { useState, useEffect } from 'react';
import {
    FaBolt,
    FaSun,
    FaThermometerHalf,
    FaShieldAlt,
    FaRulerCombined,
    FaShieldVirus,
    FaCloudSun,
    FaTh,
    FaCheck,
    FaImage,
} from 'react-icons/fa';

/* ============================================================
   Gautam Solar — G12R N-Type TOPCon Bifacial Solar Cell Page
   Brand Color: #a20000
   Max Efficiency: 25.0%
   ============================================================ */

const BRAND = '#a20000';
const BRAND_LIGHT = '#fdf2f2';
const BRAND_GRADIENT = 'linear-gradient(135deg, #a20000 0%, #d43838 100%)';

/* ============================================================
   👇👇  PUT YOUR IMAGE URLS HERE  👇👇
   ============================================================ */

/* 1) Wide banner image — recommended ~2400 × 900 px */
const BANNER_IMAGE = '';
/* e.g. '/images/g12r-banner.jpg' */

/* 2) Two images shown side by side below the banner */
const IMAGE_1 = '';
const IMAGE_2 = '';
/* e.g. '/images/cell-1.jpg' */

/* ============================================================
   Helper: useMediaQuery hook
   ============================================================ */
function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia(query);
        setMatches(mq.matches);
        const handler = (e) => setMatches(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [query]);
    return matches;
}

/* ============================================================
   Reusable: Image with branded placeholder fallback
   ============================================================ */
function SafeImage({ src, alt, label, isMobile, aspectRatio }) {
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        setErrored(false);
    }, [src]);

    if (!src || errored) {
        return (
            <div
                style={{
                    width: '100%',
                    aspectRatio: aspectRatio || '4 / 3',
                    background: 'linear-gradient(135deg, #fdf2f2 0%, #f7e0e0 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    color: BRAND,
                    border: '1px dashed rgba(162,0,0,0.35)',
                    borderRadius: '12px',
                    boxSizing: 'border-box',
                    padding: '16px',
                }}
            >
                <FaImage style={{ fontSize: isMobile ? '28px' : '38px', opacity: 0.6 }} />
                <span
                    style={{
                        fontSize: isMobile ? '12px' : '13px',
                        fontWeight: 600,
                        textAlign: 'center',
                        opacity: 0.75,
                    }}
                >
                    {label}
                </span>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            onError={() => setErrored(true)}
            style={{
                width: '100%',
                height: '100%',
                // objectFit: 'contain',
                display: 'block',
                borderRadius: '12px',
            }}
        />
    );
}

/* ============================================================
   Section: Hero Banner (with banner image)
   ============================================================ */
function HeroSection() {
    const videoRef = React.useRef(null);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = true;
        const playPromise = v.play();
        if (playPromise) {
            playPromise.catch((err) => console.warn('Autoplay blocked:', err));
        }
    }, []);

    return (
       
            <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onError={(e) => console.error('❌ Video error:', e.currentTarget.error)}
                style={{
                aspectRatio: '16 / 9',

                    width: '100%',
                    // height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            >
                <source src="/Gautam_Solar_G12R.mp4" type="video/mp4" />
                Your browser does not support the video tag.
            </video>
    );
}

/* ============================================================
   Section: Key Highlights (Metrics Bar)
   ============================================================ */
function MetricsBar() {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const metrics = [
        { value: 'High', label: 'Efficiency' },
        { value: 'Superior', label: 'Output Power' },
        { value: 'Excellent', label: 'Bifaciality Factor' },
        { value: 'Optimized', label: 'Temp. Coefficient' },
    ];

    return (
        <section
            style={{
                background: '#fff',
                borderBottom: '1px solid #eee',
                padding: isMobile ? '20px 16px' : '32px 40px',
            }}
        >
            <div
                style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: isMobile ? '16px' : '32px',
                    textAlign: 'center',
                }}
            >
                {metrics.map((m, i) => (
                    <div
                        key={i}
                        style={{
                            padding: isMobile ? '16px 8px' : '20px 16px',
                            borderRadius: '12px',
                            background: BRAND_LIGHT,
                        }}
                    >
                        <div
                            style={{
                                fontSize: isMobile ? '22px' : '32px',
                                fontWeight: 800,
                                color: BRAND,
                                lineHeight: 1.2,
                            }}
                        >
                            {m.value}
                        </div>
                        <div
                            style={{
                                fontSize: isMobile ? '11px' : '14px',
                                color: '#555',
                                marginTop: '6px',
                                fontWeight: 500,
                            }}
                        >
                            {m.label}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ============================================================
   Section: Two Images
   ============================================================ */
function TwoImagesSection() {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const images = [
        { src: '/cell-2.webp', alt: 'G12R solar cell image 1' },
        { src: '/cell-3.webp', alt: 'G12R solar cell image 2' },
    ];

    return (
        <section
            style={{
                padding: isMobile ? '40px 20px' : '64px 40px',
                background: '#fff',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: isMobile ? '16px' : '28px',
                    }}
                >
                    {images.map((img, i) => (
                        <div
                            key={i}
                            style={{
                                width: '100%',
                                aspectRatio: '4 / 3',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                border: '1px solid #f0f0f0',
                                background: '#fafafa',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                            }}
                        >
                            <SafeImage
                                src={img.src}
                                alt={img.alt}
                                label={`Put image ${i + 1} here (IMAGE_${i + 1})`}
                                isMobile={isMobile}
                                aspectRatio="4 / 3"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   Section: Product Advantages
   ============================================================ */
function AdvantagesSection() {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const advantages = [
        {
            Icon: FaBolt,
            title: 'High Conversion Efficiency',
            desc: 'Front-side efficiency reaches 25.0% with advanced TOPCon passivation, delivering a maximum power output of 9.57W per cell.',
        },
        {
            Icon: FaSun,
            title: 'Bifacial Dual-Glass Design',
            desc: 'Bifaciality factor ≥80% allows rear-side light capture, increasing energy yield by up to 25% in utility-scale applications.',
        },
        {
            Icon: FaThermometerHalf,
            title: 'Low Temperature Coefficient',
            desc: 'Power temperature coefficient of -0.29%/K ensures stable output even under India’s high-temperature conditions.',
        },
        {
            Icon: FaShieldAlt,
            title: 'Zero LID / Low LeTID',
            desc: 'N-type TOPCon structure eliminates light-induced degradation, preserving performance throughout the module’s 30-year lifetime.',
        },
        {
            Icon: FaRulerCombined,
            title: 'Optimised Rectangular Format',
            desc: '182.3mm × 210mm dimensions maximise module packing density and reduce balance-of-system costs.',
        },
        {
            Icon: FaShieldVirus,
            title: 'Superior Anti-PID Performance',
            desc: 'Excellent potential-induced degradation resistance ensures reliability in humid and coastal environments.',
        },
        {
            Icon: FaCloudSun,
            title: 'Strong Low-Light Response',
            desc: 'Relative conversion efficiency ≥92% at 200W/m² irradiance, generating more energy during cloudy and early-morning hours.',
        },
        {
            Icon: FaTh,
            title: 'Multi-Busbar Design',
            desc: '16-busbar architecture with 310 fine fingers on the front reduces shading loss and improves current collection.',
        },
    ];

    return (
        <section
            style={{
                padding: isMobile ? '48px 20px' : '80px 40px',
                background: '#fafafa',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '56px' }}>
                    <div
                        style={{
                            display: 'inline-block',
                            background: BRAND_LIGHT,
                            color: BRAND,
                            borderRadius: '20px',
                            padding: '6px 18px',
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '16px',
                        }}
                    >
                        Product Advantages
                    </div>
                    <h2
                        style={{
                            fontSize: isMobile ? '24px' : '38px',
                            fontWeight: 800,
                            color: '#1a1a1a',
                            margin: '0 0 12px 0',
                        }}
                    >
                        Engineered for Maximum Performance
                    </h2>
                    <p
                        style={{
                            fontSize: isMobile ? '14px' : '17px',
                            color: '#666',
                            maxWidth: '640px',
                            margin: '0 auto',
                            lineHeight: 1.6,
                        }}
                    >
                        The G12R cell combines N-Type TOPCon technology with a rectangular wafer format to
                        deliver high efficiency, reliability, and energy yield.
                    </p>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: isMobile ? '16px' : '24px',
                    }}
                >
                    {advantages.map((adv, i) => {
                        const IconComponent = adv.Icon;
                        return (
                            <div
                                key={i}
                                style={{
                                    background: '#fff',
                                    borderRadius: '14px',
                                    padding: isMobile ? '24px 20px' : '32px 28px',
                                    border: '1px solid #f0f0f0',
                                    display: 'flex',
                                    gap: '16px',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <div
                                    style={{
                                        flexShrink: 0,
                                        width: isMobile ? '48px' : '56px',
                                        height: isMobile ? '48px' : '56px',
                                        background: BRAND_LIGHT,
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: BRAND,
                                        fontSize: isMobile ? '20px' : '24px',
                                    }}
                                >
                                    <IconComponent />
                                </div>
                                <div>
                                    <h3
                                        style={{
                                            fontSize: isMobile ? '16px' : '18px',
                                            fontWeight: 700,
                                            color: '#1a1a1a',
                                            margin: '0 0 8px 0',
                                        }}
                                    >
                                        {adv.title}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: isMobile ? '13px' : '14px',
                                            color: '#666',
                                            lineHeight: 1.65,
                                            margin: 0,
                                        }}
                                    >
                                        {adv.desc}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   Section: Technical Specifications
   ============================================================ */
function SpecsSection() {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const specs = [
        ['Cell Type', 'N-Type TOPCon Bifacial'],
        ['Wafer Format', 'G12R (182.3mm × 210mm)'],
        ['Diagonal', '272mm ± 0.5mm'],
        ['Thickness', '130 ± 13μm'],
        ['Front Side', '16 busbar (silver), 310 fingers, blue SiNx AR coating'],
        ['Rear Side', '16 busbar (silver), 252 fingers, blue SiNx AR coating'],
        ['Efficiency Range', '23.5% – 25.0%'],
        ['Max Power Output', '9.57W (Pmpp)'],
        ['Bifaciality Factor', '≥ 80%'],
        ['Temperature Coefficient (Pmax)', '-0.29 ± 0.025%/K'],
        ['Temperature Coefficient (Voc)', '-0.25 ± 0.01%/K'],
        ['Temperature Coefficient (Isc)', '+0.04 ± 0.004%/K'],
        ['Shunt Resistance', 'Rsh ≥ 50Ω'],
        ['Reverse Current', 'Irev2 ≤ 0.5A'],
        ['Standard Test Conditions', '1000W/m², AM1.5, 25°C'],
    ];

    return (
        <section
            id="specs"
            style={{
                padding: isMobile ? '48px 20px' : '80px 40px',
                background: '#fff',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
                    <div
                        style={{
                            display: 'inline-block',
                            background: BRAND_LIGHT,
                            color: BRAND,
                            borderRadius: '20px',
                            padding: '6px 18px',
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '16px',
                        }}
                    >
                        Technical Specifications
                    </div>
                    <h2
                        style={{
                            fontSize: isMobile ? '24px' : '38px',
                            fontWeight: 800,
                            color: '#1a1a1a',
                            margin: '0 0 12px 0',
                        }}
                    >
                        G12R Cell Technical Data
                    </h2>
                    <p
                        style={{
                            fontSize: isMobile ? '14px' : '17px',
                            color: '#666',
                            maxWidth: '560px',
                            margin: '0 auto',
                            lineHeight: 1.6,
                        }}
                    >
                        All parameters measured under standard test conditions (STC): 1000W/m², AM1.5, 25°C.
                    </p>
                </div>

                <div
                    style={{
                        background: '#fafafa',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid #eee',
                    }}
                >
                    {specs.map(([label, value], i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                padding: isMobile ? '14px 18px' : '16px 28px',
                                background: i % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: i < specs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                gap: isMobile ? '4px' : '0',
                            }}
                        >
                            <div
                                style={{
                                    width: isMobile ? '100%' : '280px',
                                    flexShrink: 0,
                                    fontWeight: 600,
                                    color: '#444',
                                    fontSize: isMobile ? '13px' : '14px',
                                }}
                            >
                                {label}
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    color: '#1a1a1a',
                                    fontSize: isMobile ? '13px' : '14px',
                                    fontWeight: 500,
                                }}
                            >
                                {value}
                            </div>
                        </div>
                    ))}
                </div>

                <p
                    style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: '#999',
                        marginTop: '16px',
                        textAlign: 'center',
                        lineHeight: 1.5,
                    }}
                >
                    The above technical parameters are subject to technical changes and tests. Gautam Solar
                    reserves the right of final interpretation.
                </p>
            </div>
        </section>
    );
}

/* ============================================================
   Section: Electrical Performance Table
   ============================================================ */
function PerformanceTable() {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const frontData = [
        { eff: '25.0', pmpp: '9.57', vmpp: '0.630', impp: '15.19', uoc: '0.745', isc: '15.45', ff: '83.10' },
        { eff: '24.9', pmpp: '9.53', vmpp: '0.629', impp: '15.15', uoc: '0.744', isc: '15.42', ff: '83.00' },
        { eff: '24.8', pmpp: '9.49', vmpp: '0.628', impp: '15.11', uoc: '0.743', isc: '15.39', ff: '82.90' },
        { eff: '24.7', pmpp: '9.45', vmpp: '0.627', impp: '15.07', uoc: '0.742', isc: '15.36', ff: '82.80' },
        { eff: '24.6', pmpp: '9.41', vmpp: '0.626', impp: '15.03', uoc: '0.741', isc: '15.33', ff: '82.70' },
        { eff: '24.5', pmpp: '9.38', vmpp: '0.625', impp: '15.01', uoc: '0.740', isc: '15.30', ff: '82.60' },
        { eff: '24.4', pmpp: '9.34', vmpp: '0.624', impp: '14.97', uoc: '0.739', isc: '15.27', ff: '82.50' },
        { eff: '24.3', pmpp: '9.30', vmpp: '0.623', impp: '14.93', uoc: '0.738', isc: '15.24', ff: '82.40' },
        { eff: '24.2', pmpp: '9.26', vmpp: '0.622', impp: '14.89', uoc: '0.737', isc: '15.21', ff: '82.30' },
        { eff: '24.1', pmpp: '9.22', vmpp: '0.621', impp: '14.85', uoc: '0.736', isc: '15.18', ff: '82.20' },
        { eff: '24.0', pmpp: '9.18', vmpp: '0.620', impp: '14.81', uoc: '0.735', isc: '15.15', ff: '82.10' },
        { eff: '23.9', pmpp: '9.15', vmpp: '0.619', impp: '14.78', uoc: '0.734', isc: '15.12', ff: '82.00' },
        { eff: '23.8', pmpp: '9.11', vmpp: '0.618', impp: '14.74', uoc: '0.733', isc: '15.09', ff: '81.90' },
        { eff: '23.7', pmpp: '9.07', vmpp: '0.617', impp: '14.70', uoc: '0.732', isc: '15.06', ff: '81.80' },
        { eff: '23.6', pmpp: '9.03', vmpp: '0.616', impp: '14.66', uoc: '0.731', isc: '15.03', ff: '81.70' },
        { eff: '23.5', pmpp: '8.99', vmpp: '0.615', impp: '14.62', uoc: '0.730', isc: '15.00', ff: '81.60' },
    ];

    const rearData = [
        { eff: '≥19.8', pmpp: '7.66', umpp: '0.620', impp: '12.35', uoc: '0.735', isc: '12.58', ff: '82.90' },
        { eff: '19.6-19.8', pmpp: '7.58', umpp: '0.616', impp: '12.30', uoc: '0.732', isc: '12.52', ff: '82.70' },
        { eff: '19.4-19.6', pmpp: '7.50', umpp: '0.612', impp: '12.25', uoc: '0.729', isc: '12.46', ff: '82.50' },
        { eff: '19.2-19.4', pmpp: '7.42', umpp: '0.608', impp: '12.20', uoc: '0.726', isc: '12.40', ff: '82.30' },
        { eff: '19.0-19.2', pmpp: '7.34', umpp: '0.604', impp: '12.15', uoc: '0.723', isc: '12.34', ff: '82.10' },
    ];

    const tableHeaderStyle = {
        background: BRAND,
        color: '#fff',
        fontWeight: 700,
        fontSize: isMobile ? '10px' : '12px',
        padding: isMobile ? '8px 4px' : '12px 8px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
    };

    const tableCellStyle = (i) => ({
        padding: isMobile ? '7px 4px' : '10px 8px',
        textAlign: 'center',
        fontSize: isMobile ? '11px' : '13px',
        borderBottom: '1px solid #f0f0f0',
        background: i % 2 === 0 ? '#fff' : '#fafafa',
        color: '#333',
    });

    const renderTable = (data, headers, keys) => (
        <div
            style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '12px',
                border: '1px solid #eee',
                marginBottom: '32px',
            }}
        >
            <table
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    minWidth: isMobile ? '640px' : 'auto',
                }}
            >
                <thead>
                    <tr>
                        {headers.map((h, i) => (
                            <th key={i} style={tableHeaderStyle}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i}>
                            {keys.map((k, j) => (
                                <td key={j} style={tableCellStyle(i)}>
                                    {row[k]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <section
            style={{
                padding: isMobile ? '48px 20px' : '80px 40px',
                background: '#fafafa',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
                    <div
                        style={{
                            display: 'inline-block',
                            background: BRAND_LIGHT,
                            color: BRAND,
                            borderRadius: '20px',
                            padding: '6px 18px',
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '16px',
                        }}
                    >
                        IV Curve Data
                    </div>
                    <h2
                        style={{
                            fontSize: isMobile ? '24px' : '38px',
                            fontWeight: 800,
                            color: '#1a1a1a',
                            margin: '0 0 12px 0',
                        }}
                    >
                        Electrical Performance Distribution
                    </h2>
                    <p
                        style={{
                            fontSize: isMobile ? '14px' : '17px',
                            color: '#666',
                            maxWidth: '560px',
                            margin: '0 auto',
                            lineHeight: 1.6,
                        }}
                    >
                        Standard test conditions: 1000W/m², AM1.5, 25°C
                    </p>
                </div>

                <h3
                    style={{
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: 700,
                        color: BRAND,
                        margin: '0 0 16px 0',
                        paddingLeft: '4px',
                        borderLeft: `4px solid ${BRAND}`,
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        paddingRight: '12px',
                    }}
                >
                    Front Side (-) Performance
                </h3>
                {renderTable(
                    frontData,
                    ['Eff (%)', 'Pmpp (W)', 'Vmpp (V)', 'Impp (A)', 'Uoc (V)', 'Isc (A)', 'FF (%)'],
                    ['eff', 'pmpp', 'vmpp', 'impp', 'uoc', 'isc', 'ff']
                )}

                <h3
                    style={{
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: 700,
                        color: BRAND,
                        margin: '0 0 16px 0',
                        paddingLeft: '4px',
                        borderLeft: `4px solid ${BRAND}`,
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        paddingRight: '12px',
                    }}
                >
                    Rear Side (+) Performance
                </h3>
                {renderTable(
                    rearData,
                    ['Eff (%)', 'Pmpp (W)', 'Umpp (V)', 'Impp (A)', 'Uoc (V)', 'Isc (A)', 'FF (%)'],
                    ['eff', 'pmpp', 'umpp', 'impp', 'uoc', 'isc', 'ff']
                )}

                <p
                    style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: '#999',
                        textAlign: 'center',
                        lineHeight: 1.5,
                    }}
                >
                    The above technical parameters are subject to technical changes and tests. Gautam Solar
                    reserves the right of final interpretation.
                </p>
            </div>
        </section>
    );
}

/* ============================================================
   Section: Certifications & Why Gautam Solar
   ============================================================ */
function CertificationsSection() {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const certs = [
        { label: 'ALMM Listed', sub: 'MNRE Approved' },
        { label: 'BIS Certified', sub: 'IS 14286' },
        { label: 'IEC 61215', sub: 'Design Qualification' },
        { label: 'IEC 61730', sub: 'Safety Qualification' },
        { label: 'ISO 9001', sub: 'Quality Management' },
        { label: 'ISO 14001', sub: 'Environmental' },
    ];

    const whyPoints = [
        '29+ years of solar manufacturing experience',
        "Among India's top 4 solar module manufacturers",
        '4 manufacturing facilities across India',
        '5 GW module capacity with upcoming cell plant',
        'In-house R&D with multiple patents & IPs',
        'KIWA PVEL Top Performer 2026 recognised',
    ];

    return (
        <section
            style={{
                padding: isMobile ? '48px 20px' : '80px 40px',
                background: '#fff',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: isMobile ? '40px' : '64px',
                        alignItems: 'start',
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: 'inline-block',
                                background: BRAND_LIGHT,
                                color: BRAND,
                                borderRadius: '20px',
                                padding: '6px 18px',
                                fontSize: isMobile ? '12px' : '13px',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                marginBottom: '16px',
                            }}
                        >
                            Why Gautam Solar
                        </div>
                        <h2
                            style={{
                                fontSize: isMobile ? '24px' : '34px',
                                fontWeight: 800,
                                color: '#1a1a1a',
                                margin: '0 0 24px 0',
                                lineHeight: 1.2,
                            }}
                        >
                            Trusted by India's
                            <br />
                            Solar Industry
                        </h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {whyPoints.map((point, i) => (
                                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <div
                                        style={{
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '50%',
                                            background: BRAND,
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            flexShrink: 0,
                                            marginTop: '1px',
                                        }}
                                    >
                                        <FaCheck />
                                    </div>
                                    <span
                                        style={{
                                            fontSize: isMobile ? '14px' : '15px',
                                            color: '#444',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {point}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div
                            style={{
                                display: 'inline-block',
                                background: BRAND_LIGHT,
                                color: BRAND,
                                borderRadius: '20px',
                                padding: '6px 18px',
                                fontSize: isMobile ? '12px' : '13px',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                marginBottom: '16px',
                            }}
                        >
                            Certifications
                        </div>
                        <h2
                            style={{
                                fontSize: isMobile ? '24px' : '34px',
                                fontWeight: 800,
                                color: '#1a1a1a',
                                margin: '0 0 24px 0',
                                lineHeight: 1.2,
                            }}
                        >
                            Quality You Can
                            <br />
                            Rely On
                        </h2>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                gap: '12px',
                            }}
                        >
                            {certs.map((cert, i) => (
                                <div
                                    key={i}
                                    style={{
                                        background: '#fafafa',
                                        borderRadius: '10px',
                                        padding: isMobile ? '16px 10px' : '20px 14px',
                                        textAlign: 'center',
                                        border: '1px solid #f0f0f0',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: isMobile ? '12px' : '13px',
                                            fontWeight: 700,
                                            color: BRAND,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {cert.label}
                                    </div>
                                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#888' }}>
                                        {cert.sub}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   Section: CTA / Contact
   ============================================================ */


/* ============================================================
   Footer
   ============================================================ */


/* ============================================================
   Main Page Component
   ============================================================ */
export default function G12RSolarCellPage() {
    return (
        <div
            style={{
                fontFamily:
                    "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
                margin: 0,
                padding: '10px',
                WebkitFontSmoothing: 'antialiased',
                overflowX: 'hidden',
            }}
        >
            <HeroSection />
            <MetricsBar />
            <TwoImagesSection />
            <AdvantagesSection />
            <SpecsSection />
            <PerformanceTable />
            <CertificationsSection />
            {/* <ContactSection /> */}
            {/* <Footer /> */}
        </div>
    );
}